// Cart Validation for Birthday Add-on Gifts
// This script prevents checkout when add-on gifts are in cart without paid items

class CartValidation {
  constructor() {
    this.isValidating = false;
    this.lastValidationResult = null;

    // Immediately remove any existing error messages
    const existingError = document.getElementById("addon-validation-error");
    if (existingError) {
      existingError.remove();
    }

    this.init();
  }

  init() {
    // Immediately clear any existing error messages on script initialization
    this.hideAddonValidationError();

    // Setup checkout button interception (includes cart listeners and validation)
    this.setupCheckoutInterception();

    // Setup quantity validation for birthday gifts
    this.setupQuantityValidation();
  }

  setupCartListeners() {
    // Listen for cart updates with a delay to avoid interfering with loading states
    document.addEventListener("cart:updated", () => {
      // Add a small delay to let the cart update complete first
      setTimeout(() => {
        this.validateCartForButtonState();
      }, 100);
    });

    // Listen for mini cart updates with a delay
    document.addEventListener("cart:refresh", () => {
      // Add a small delay to let the cart update complete first
      setTimeout(() => {
        this.validateCartForButtonState();
      }, 100);
    });

    // Listen for cart drawer updates with a delay
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      cartDrawer.addEventListener("cart:updated", () => {
        // Add a small delay to let the cart update complete first
        setTimeout(() => {
          this.validateCartForButtonState();
        }, 100);
      });
    }

    // Listen for mini cart close events
    this.setupMiniCartCloseListeners();

    // Quantity protection removed - keeping it simple
  }

  setupMiniCartCloseListeners() {
    // Listen for cart drawer close events
    document.addEventListener("cart-drawer:close", () => {
      this.hideAddonValidationError();
    });

    // Listen for cart drawer toggle events (close)
    document.addEventListener("cart-drawer:toggle", (event) => {
      // Check if the cart drawer is now closed
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && !cartDrawer.hasAttribute("open")) {
        this.hideAddonValidationError();
      }
    });

    // Listen for ESC key (common way to close mini cart)
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.hideAddonValidationError();
      }
    });

    // Listen for clicks outside cart drawer (if it closes)
    document.addEventListener("click", (event) => {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && cartDrawer.hasAttribute("open")) {
        // Check if click is outside the cart drawer
        if (
          !cartDrawer.contains(event.target) &&
          !event.target.closest("[data-cart-trigger]")
        ) {
          this.hideAddonValidationError();
        }
      }
    });

    // Listen for common cart close button clicks
    document.addEventListener("click", (event) => {
      const closeButton = event.target.closest(
        "[data-cart-close], .cart-close, .cart-drawer-close, [aria-label*='close'], [aria-label*='Close']",
      );
      if (closeButton) {
        this.hideAddonValidationError();
      }
    });

    // Listen for overlay clicks (common cart close pattern)
    document.addEventListener("click", (event) => {
      if (
        event.target.classList.contains("cart-drawer-overlay") ||
        event.target.classList.contains("cart-overlay") ||
        event.target.classList.contains("drawer-overlay")
      ) {
        this.hideAddonValidationError();
      }
    });

    // Listen for any click that might close the cart (more aggressive)
    document.addEventListener("click", (event) => {
      // Check if this click might close the cart
      const target = event.target;
      const cartDrawer = document.querySelector("cart-drawer");

      if (cartDrawer && cartDrawer.hasAttribute("open")) {
        // If click is on cart drawer but not on cart content, it might be closing
        if (cartDrawer.contains(target)) {
          const cartContent = cartDrawer.querySelector(
            ".cart-drawer__content, .cart-content, .drawer__content",
          );
          if (!cartContent || !cartContent.contains(target)) {
            // Add a small delay to see if cart actually closes
            setTimeout(() => {
              if (!cartDrawer.hasAttribute("open")) {
                this.hideAddonValidationError();
              }
            }, 50);
          }
        }
      }
    });

    // Monitor cart drawer state changes with MutationObserver
    this.monitorCartDrawerState();

    // Also use polling as a fallback
    this.startCartDrawerPolling();
  }

  monitorCartDrawerState() {
    // Watch for changes to cart drawer attributes and classes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" || mutation.type === "classList") {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            // Check if cart drawer is now closed
            const isOpen =
              cartDrawer.hasAttribute("open") ||
              cartDrawer.classList.contains("open") ||
              cartDrawer.classList.contains("active") ||
              cartDrawer.style.display !== "none";

            if (!isOpen) {
              console.log(
                "🛒 Cart drawer state changed to closed - hiding error message",
              );
              this.hideAddonValidationError();
            }
          }
        }
      });
    });

    // Start observing cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ["open", "class", "style"],
        subtree: false,
      });
    }

    // Monitor body class changes for cart open/close
    const bodyClassObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const body = mutation.target;
          if (body === document.body) {
            const cartOpenClasses = [
              "mini-cart--open",
              "cart-open",
              "drawer-open",
              "cart-drawer-open",
              "mini-cart-open",
            ];

            const hasCartOpenClass = cartOpenClasses.some((className) =>
              body.classList.contains(className),
            );

            if (!hasCartOpenClass) {
              this.hideAddonValidationError();
            }
          }
        }
      });
    });

    bodyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Also monitor for cart drawer being added to DOM
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (
              node.tagName === "CART-DRAWER" ||
              node.querySelector("cart-drawer")
            ) {
              this.monitorCartDrawerState();
            }
          }
        });
      });
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  startCartDrawerPolling() {
    let lastCartDrawerState = null;

    const checkCartDrawerState = () => {
      const cartDrawer = document.querySelector("cart-drawer");

      // Check body for cart open classes
      const bodyCartOpen =
        document.body.classList.contains("mini-cart--open") ||
        document.body.classList.contains("cart-open") ||
        document.body.classList.contains("drawer-open") ||
        document.body.classList.contains("cart-drawer-open") ||
        document.body.classList.contains("mini-cart-open");

      if (cartDrawer) {
        // Check multiple indicators of cart drawer being open
        const cartDrawerOpen =
          cartDrawer.hasAttribute("open") ||
          cartDrawer.classList.contains("open") ||
          cartDrawer.classList.contains("active") ||
          cartDrawer.classList.contains("visible") ||
          cartDrawer.style.display !== "none" ||
          cartDrawer.style.visibility !== "hidden" ||
          cartDrawer.style.opacity !== "0" ||
          window.getComputedStyle(cartDrawer).display !== "none" ||
          window.getComputedStyle(cartDrawer).visibility !== "hidden";

        const isOpen = cartDrawerOpen || bodyCartOpen;

        // If state changed from open to closed, hide error message
        if (lastCartDrawerState === true && !isOpen) {
          this.hideAddonValidationError();
        }

        lastCartDrawerState = isOpen;
      } else if (bodyCartOpen) {
        // If no cart drawer element but body has cart open class
        if (lastCartDrawerState === true && !bodyCartOpen) {
          this.hideAddonValidationError();
        }
        lastCartDrawerState = bodyCartOpen;
      }
    };

    // Check immediately
    checkCartDrawerState();

    // Then check every 100ms
    setInterval(checkCartDrawerState, 100);
  }

  setupCheckoutInterception() {
    // Validate cart on page load
    this.validateCartOnPageLoad();

    // Watch for cart changes and re-validate
    this.setupCartListeners();

    // Intercept checkout button clicks to prevent navigation if validation fails
    this.interceptCheckoutButtons();
  }

  interceptCheckoutButtons() {
    // Track if we're already processing a checkout click to prevent duplicates
    let isProcessingCheckout = false;

    // Intercept checkout button clicks with higher priority
    document.addEventListener(
      "click",
      async (event) => {
        const checkoutButton = event.target.closest(
          'button[name="checkout"], input[name="checkout"], [href*="/checkout"], .checkout-button, [data-action="checkout"]',
        );

        if (checkoutButton && !isProcessingCheckout) {
          isProcessingCheckout = true;

          console.log(
            "🛒 Cart validation: Checkout button clicked",
            checkoutButton,
          );

          try {
            // Validate cart first
            const startTime = performance.now();
            const isValid = await this.validateCart();
            const endTime = performance.now();

            console.log(
              `🛒 Cart validation: Validation completed in ${(
                endTime - startTime
              ).toFixed(2)}ms`,
            );
            console.log("🛒 Cart validation: Validation result:", isValid);

            if (isValid) {
              console.log("🛒 Cart validation: Allowing checkout");
              // Allow the original form submission to proceed naturally
              // Don't prevent default - let the form submit normally
            } else {
              console.log(
                "🛒 Cart validation: Blocking checkout - showing error",
              );
              // Prevent the default navigation and show error
              event.preventDefault();
              event.stopPropagation();
            }
          } finally {
            // Reset processing flag after a short delay to prevent immediate re-triggering
            setTimeout(() => {
              isProcessingCheckout = false;
            }, 100);
          }
        }
      },
      true,
    ); // Use capture phase to ensure we run first

    // Also intercept form submissions with higher priority
    document.addEventListener(
      "submit",
      async (event) => {
        const form = event.target;
        if (form.action && form.action.includes("checkout")) {
          console.log(
            "🛒 Cart validation: Checkout form submission intercepted",
          );

          // Prevent the default submission
          event.preventDefault();
          event.stopPropagation();

          // Validate cart
          const isValid = await this.validateCart();
          console.log("🛒 Cart validation: Form validation result:", isValid);

          if (isValid) {
            console.log("🛒 Cart validation: Allowing form submission");
            // Allow form submission
            form.submit();
          } else {
            console.log(
              "🛒 Cart validation: Blocking form submission - showing error",
            );
            // Don't submit, error message is already shown
          }
        }
      },
      true,
    ); // Use capture phase to ensure we run first
  }

  async validateCartOnPageLoad() {
    // Clear any existing error messages on page load
    this.hideAddonValidationError();

    // Only update checkout button state, don't show error messages on page load
    await this.validateCartForButtonState();
  }

  async validateCartAndUpdateCheckoutButton() {
    const isValid = await this.validateCart();
    this.updateCheckoutButtonState(isValid);
  }

  async validateCartForButtonState() {
    // Validate cart without showing error messages (for page load)
    try {
      const response = await fetch("/cart.js");
      const cartData = await response.json();

      const validation = this.analyzeCart(cartData);

      // Simplified validation logic - only care about add-on rewards vs paid products
      const isValid = !(
        validation.hasAddonRewards &&
        !validation.hasRegularProducts &&
        !validation.hasStandaloneRewards
      );

      this.updateCheckoutButtonState(isValid);
      return isValid;
    } catch (error) {
      console.error("❌ Error validating cart for button state:", error);
      this.updateCheckoutButtonState(true); // Allow checkout if validation fails
      return true;
    }
  }

  updateCheckoutButtonState(isValid) {
    // Find checkout buttons
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], [href*="/checkout"]',
    );

    checkoutButtons.forEach((button) => {
      if (!isValid) {
        // Add visual indication that checkout might be blocked
        button.setAttribute(
          "title",
          "Add-on gifts require other items in cart",
        );

        // Don't disable the button - let the click event handle validation
        // This allows users to click and see the error message
      } else {
        // Remove visual indication
        button.removeAttribute("title");
      }
    });
  }

  validateCart() {
    // Click and submit handlers both validate on the same checkout click; share
    // the in-flight result so the submit handler never sees a stale/null value.
    if (this.validationPromise) {
      return this.validationPromise;
    }

    this.validationPromise = this.runCartValidation().finally(() => {
      this.validationPromise = null;
    });
    return this.validationPromise;
  }

  async runCartValidation() {
    this.isValidating = true;

    try {
      // Try to get cart data from DOM first (much faster)
      console.log("🛒 Cart validation: Reading cart data from DOM");
      const cartData = this.getCartDataFromDOM();

      if (cartData && cartData.items && cartData.items.length > 0) {
        console.log("🛒 Cart validation: Successfully read cart data from DOM");
        const validation = this.analyzeCart(cartData);

        if (
          validation.hasAddonRewards &&
          !validation.hasRegularProducts &&
          !validation.hasStandaloneRewards
        ) {
          this.showAddonValidationError();
          this.lastValidationResult = false;
          return false;
        }

        this.hideAddonValidationError();
        this.lastValidationResult = true;
        return true;
      }

      // Fallback to API if DOM data not available
      console.log(
        "🛒 Cart validation: DOM data not available, falling back to API",
      );
      const response = await fetch("/cart.js");
      const apiCartData = await response.json();

      const validation = this.analyzeCart(apiCartData);

      if (
        validation.hasAddonRewards &&
        !validation.hasRegularProducts &&
        !validation.hasStandaloneRewards
      ) {
        this.showAddonValidationError();
        this.lastValidationResult = false;
        return false;
      }

      this.hideAddonValidationError();
      this.lastValidationResult = true;
      return true;
    } catch (error) {
      console.error("❌ Error validating cart:", error);
      this.lastValidationResult = true;
      return true; // Allow checkout if validation fails
    } finally {
      this.isValidating = false;
    }
  }

  getCartDataFromDOM() {
    try {
      // Get cart items from the DOM
      const cartItems = document.querySelectorAll(
        ".cart-item, .cart__item, [data-cart-item]",
      );

      if (cartItems.length === 0) {
        return null;
      }

      const items = [];

      cartItems.forEach((cartItem, index) => {
        // Get item title
        const titleElement = cartItem.querySelector(
          ".cart-item__name, .cart__item-name, .cart-item-title, h3, h4",
        );
        const title = titleElement ? titleElement.textContent.trim() : "";

        // Get item properties from the DOM
        const properties = {};

        // Look for property elements in the cart item
        const propertyElements = cartItem.querySelectorAll(
          ".product-option, [data-property]",
        );
        propertyElements.forEach((propElement) => {
          const dt = propElement.querySelector("dt");
          const dd = propElement.querySelector("dd");
          if (dt && dd) {
            const key = dt.textContent.replace(":", "").trim();
            const value = dd.textContent.trim();
            properties[key] = value;
          }
        });

        // Also check for any hidden input fields with properties
        const hiddenInputs = cartItem.querySelectorAll('input[type="hidden"]');
        hiddenInputs.forEach((input) => {
          const name = input.name;
          if (name && name.includes("properties[")) {
            const key = name.replace("properties[", "").replace("]", "");
            properties[key] = input.value;
          }
        });

        items.push({
          title: title,
          properties: properties,
          index: index,
        });
      });

      return { items: items };
    } catch (error) {
      console.error("❌ Error reading cart data from DOM:", error);
      return null;
    }
  }

  analyzeCart(cartData) {
    // Check for add-on reward products (including add-on bundles)
    const addonRewards = cartData.items.filter(
      (item) =>
        item.properties &&
        Object.keys(item.properties).some(
          (key) =>
            key.includes("Birthday Gift") &&
            key.includes(
              "This gift can only be redeemed alongside a purchase",
            ) &&
            item.properties[key] === "✓",
        ),
    );

    // Check for standalone reward products (including standalone bundles)
    const standaloneRewards = cartData.items.filter(
      (item) =>
        item.properties &&
        Object.keys(item.properties).some(
          (key) => key === "Birthday Gift" && item.properties[key] === "✓",
        ) &&
        !Object.keys(item.properties).some(
          (key) =>
            key.includes("Birthday Gift") &&
            key.includes(
              "This gift can only be redeemed alongside a purchase",
            ) &&
            item.properties[key] === "✓",
        ),
    );

    // Check for regular products (non-reward products)
    const regularProducts = cartData.items.filter(
      (item) =>
        !item.properties ||
        !Object.keys(item.properties).some(
          (key) =>
            key.includes("Birthday Gift") && item.properties[key] === "✓",
        ),
    );

    return {
      hasAddonRewards: addonRewards.length > 0,
      hasStandaloneRewards: standaloneRewards.length > 0,
      hasRegularProducts: regularProducts.length > 0,
      addonRewards: addonRewards,
      standaloneRewards: standaloneRewards,
      regularProducts: regularProducts,
    };
  }

  showAddonValidationError() {
    // Check if error is already showing
    const existingError = document.getElementById("addon-validation-error");
    if (existingError) {
      return;
    }

    // Create error message
    const errorDiv = document.createElement("div");
    errorDiv.id = "addon-validation-error";
    errorDiv.className = "addon-validation-error";
    errorDiv.innerHTML = `
      <div class="addon-validation-message">
        <div class="addon-validation-icon">⚠️</div>
        <div class="addon-validation-content">
          <h4>Please add an item into your cart</h4>
          <p>our gift can only be redeemed with the purchase of 1 or more items.</p>
          <button class="addon-validation-close" onclick="this.parentElement.parentElement.parentElement.remove()">×</button>
        </div>
      </div>
    `;

    // Add styles
    this.addValidationStyles();

    // Insert error message
    this.insertErrorInPage(errorDiv);
  }

  hideAddonValidationError() {
    const existingError = document.getElementById("addon-validation-error");
    if (existingError) {
      existingError.remove();
    }
  }

  addValidationStyles() {
    if (document.getElementById("addon-validation-styles")) {
      return; // Styles already added
    }

    const styles = document.createElement("style");
    styles.id = "addon-validation-styles";
    styles.textContent = `
      .addon-validation-error {
        position: fixed;
        bottom: 35vh;
        right: 10px;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 8px;
        padding: 16px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 380px;
        animation: slideInRight 0.3s ease-out;
      }

      .addon-validation-message {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        position: relative;
      }

      .addon-validation-icon {
        font-size: 20px;
        flex-shrink: 0;
      }

      .addon-validation-content {
        flex: 1;
      }

      .addon-validation-content h4 {
        margin: 0 0 8px 0;
        color: #856404;
        font-size: 16px;
        font-weight: 600;
      }

      .addon-validation-content p {
        margin: 0;
        color: #856404;
        font-size: 14px;
        line-height: 1.4;
      }

      .addon-validation-close {
        background: none;
        border: none;
        font-size: 18px;
        color: #856404;
        cursor: pointer;
        padding: 0;
        line-height: 1;
        position: absolute;
        top: 0;
        right: 0;
      }

      .addon-validation-close:hover {
        color: #533f03;
      }

      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .checkout-disabled {
        opacity: 0.5;
        pointer-events: none;
      }
    `;

    document.head.appendChild(styles);
  }

  insertErrorInPage(errorDiv) {
    // Try to insert in cart page
    const cartContainer = document.querySelector(
      ".cart, .cart-page, .cart__container",
    );
    if (cartContainer) {
      cartContainer.insertBefore(errorDiv, cartContainer.firstChild);
      return;
    }

    // Try to insert in mini cart
    const miniCart = document.querySelector(
      "cart-drawer, .mini-cart, .cart-drawer",
    );
    if (miniCart) {
      miniCart.appendChild(errorDiv);
      return;
    }

    // Fallback to body
    document.body.appendChild(errorDiv);
  }

  // Simple quantity validation for birthday gifts
  setupQuantityValidation() {
    // Initial setup
    this.disableBirthdayGiftQuantities();

    // Listen for cart updates to re-apply quantity restrictions with delay
    document.addEventListener("cart:updated", () => {
      // Add a small delay to let the cart update complete first
      setTimeout(() => {
        this.disableBirthdayGiftQuantities();
      }, 100);
    });

    // Listen for cart drawer updates with delay
    document.addEventListener("cart:refresh", () => {
      // Add a small delay to let the cart update complete first
      setTimeout(() => {
        this.disableBirthdayGiftQuantities();
      }, 100);
    });

    // Also monitor for dynamic cart content changes
    const observer = new MutationObserver(() => {
      // Add a small delay to avoid interfering with cart updates
      setTimeout(() => {
        this.disableBirthdayGiftQuantities();
      }, 50);
    });

    // Observe cart-related containers
    const cartContainers = document.querySelectorAll(
      "cart-drawer, .cart, .cart-page, .cart__container",
    );
    cartContainers.forEach((container) => {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  disableBirthdayGiftQuantities() {
    // Get current cart data to identify birthday gifts
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cartData) => {
        const birthdayGiftItems = cartData.items.filter(
          (item) =>
            item.properties &&
            Object.keys(item.properties).some(
              (key) =>
                key.includes("Birthday Gift") && item.properties[key] === "✓",
            ),
        );

        const bundleItems = cartData.items.filter(
          (item) =>
            item.properties &&
            Object.keys(item.properties).some(
              (key) => key === "Bundle Item" && item.properties[key],
            ),
        );

        // For each birthday gift item, find and disable its quantity controls
        birthdayGiftItems.forEach((giftItem) => {
          this.disableQuantityForItem(giftItem);
        });

        // For each bundle item, find and disable its quantity controls
        bundleItems.forEach((bundleItem) => {
          this.disableQuantityForItem(bundleItem);
        });
      })
      .catch((error) => {
        console.error(
          "❌ Error fetching cart data for quantity validation:",
          error,
        );
      });
  }

  disableQuantityForItem(giftItem) {
    // Try multiple approaches to find the cart item element

    // Approach 1: Try by cart item key
    const cartItemKey = giftItem.key;
    let cartItemElement = document.querySelector(
      `[data-cart-item-key="${cartItemKey}"], [data-cart-item="${cartItemKey}"], [data-line-item-key="${cartItemKey}"]`,
    );

    // Approach 2: Try by variant ID (this is what worked!)
    if (!cartItemElement) {
      const variantId = giftItem.variant_id;
      cartItemElement = document.querySelector(
        `[data-variant-id="${variantId}"]`,
      );
    }

    // Approach 3: Try by product ID
    if (!cartItemElement) {
      const productId = giftItem.product_id;
      cartItemElement = document.querySelector(
        `[data-product-id="${productId}"]`,
      );
    }

    // Approach 4: Try by index (if available)
    if (!cartItemElement && giftItem.index) {
      cartItemElement = document.querySelector(
        `[data-index="${giftItem.index}"]`,
      );
    }

    if (cartItemElement) {
      // Disable quantity controls within this cart item
      this.disableQuantityControlsInElement(cartItemElement, giftItem);
    } else {
      // Fallback: try to find by product title (but this is less reliable)
      const cartItems = document.querySelectorAll(
        ".cart-item, .cart__item, [data-cart-item]",
      );
      cartItems.forEach((cartItem) => {
        const itemTitle = cartItem.querySelector(
          ".cart-item__name, .cart__item-name, .cart-item-title",
        );
        if (
          itemTitle &&
          itemTitle.textContent.trim().includes(giftItem.title.split(" - ")[0])
        ) {
          this.disableQuantityControlsInElement(cartItem, giftItem);
        }
      });
    }
  }

  disableQuantityControlsInElement(element, giftItem) {
    // Find and disable quantity buttons
    const minusBtn = element.querySelector(
      'button[name="minus"], .quantity__button[name="minus"]',
    );
    const plusBtn = element.querySelector(
      'button[name="plus"], .quantity__button[name="plus"]',
    );

    if (minusBtn) {
      minusBtn.disabled = true;
      minusBtn.style.opacity = "0.5";
      minusBtn.style.pointerEvents = "none";
    }

    if (plusBtn) {
      plusBtn.disabled = true;
      plusBtn.style.opacity = "0.5";
      plusBtn.style.pointerEvents = "none";
    }

    // Disable and hide quantity input
    const quantityInput = element.querySelector(
      '.quantity__input, input[type="number"]',
    );
    if (quantityInput) {
      quantityInput.disabled = true;
      quantityInput.style.display = "none";
    }

    // Hide the entire quantity wrapper
    const quantityWrapper = element.querySelector("quantity-input, .quantity");
    if (quantityWrapper) {
      quantityWrapper.style.display = "none";
    }

    // Note: Remove button is now allowed for birthday gifts so users can remove them
  }
}

// Clean up any existing error messages immediately
const cleanupExistingErrors = () => {
  const existingError = document.getElementById("addon-validation-error");
  if (existingError) {
    existingError.remove();
  }
};

// Clean up immediately
cleanupExistingErrors();

// Initialize cart validation when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  cleanupExistingErrors(); // Clean up again in case DOM changed
  window.cartValidation = new CartValidation();
});

// Also initialize if DOM is already loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    cleanupExistingErrors(); // Clean up again in case DOM changed
    window.cartValidation = new CartValidation();
  });
} else {
  cleanupExistingErrors(); // Clean up again in case DOM changed
  window.cartValidation = new CartValidation();
}
